"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Language, Translations } from "./types";
import { en } from "./locales/en";
import { fr } from "./locales/fr";

const locales: Record<Language, Translations> = { en, fr };

const STORAGE_KEY = "744-lang";

function detectLanguage(): Language {
    if (typeof window === "undefined") return "fr";
    const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored && stored in locales) return stored;
    const browser = navigator.language.slice(0, 2).toLowerCase();
    return browser === "en" ? "en" : "fr";
}

interface LanguageContextValue {
    lang: Language;
    setLang: (lang: Language) => void;
    t: Translations;
}

const LanguageContext = createContext<LanguageContextValue>({
    lang: "fr",
    setLang: () => {},
    t: fr,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState<Language>("fr");

    useEffect(() => {
        setLangState(detectLanguage());
    }, []);

    const setLang = (next: Language) => {
        setLangState(next);
        localStorage.setItem(STORAGE_KEY, next);
    };

    return (
        <LanguageContext.Provider value={{ lang, setLang, t: locales[lang] }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useTranslation() {
    return useContext(LanguageContext);
}
